import React, {JSX, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {allCountries, Country} from "country-telephone-data";
import {PhoneNumberDTO} from "../../models/phoneNumber";
import {upsertAccountContactData} from "../../api/accountContactDataApi";
import {PhoneNumberRow} from "../components/PhoneNumberRow";
import {dialOptions, isoToFlag} from "../../utils/functions";
import MessageBox, {MessageType} from "../components/MessageBox";
import {useRequiredAccount} from "../../hooks/AccountContext";
import {useSaveHandler} from "../../hooks/SaveHandlerContext";

interface FormData {
    street: string;
    number: string;
    zip: string;
    city: string;
    country: string;
    shareData: boolean;
    phoneNumbers: PhoneNumberDTO[];
}

interface ContactDataProps {
}

function ContactData(): JSX.Element {
    const {t} = useTranslation();

    const {
        account,
        contactData: accountContactData,
        setContactData: setAccountContactData,
    } = useRequiredAccount();

    const [formData, setFormData] = useState<FormData>({
        street: "",
        number: "",
        zip: "",
        city: "",
        country: "",
        shareData: false,
        phoneNumbers: [{countryCode: "", phoneNumber: ""}],
    });

    const [message, setMessage] = useState<MessageType>(null);
    const [disableSaving, setDisableSaving] = useState<boolean>(true);

    const countryOptions = useMemo(
        () => [...allCountries].sort((a: Country, b: Country) => a.name.localeCompare(b.name)),
        []
    );

    const { setHasUnsavedChanges } = useSaveHandler();

    useEffect(() => {
        if (!accountContactData) return;

        setFormData({
            street: accountContactData.street ?? "",
            number: accountContactData.number ?? "",
            zip: accountContactData.zip ?? "",
            city: accountContactData.city ?? "",
            country: accountContactData.country ?? "",
            shareData: accountContactData.shareData ?? true,
            phoneNumbers:
                accountContactData.phoneNumbers?.length > 0
                    ? accountContactData.phoneNumbers
                    : [{countryCode: "", phoneNumber: ""}],
        });
    }, [accountContactData]);


    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
        index?: number
    ) => {
        const {name, value, type} = e.target;
        const isCheckbox = type === "checkbox";
        const checked = isCheckbox ? (e.target as HTMLInputElement).checked : undefined;

        setDisableSaving(false);
        setHasUnsavedChanges(true);

        if (index !== undefined) {
            const updatedPhones = [...formData.phoneNumbers];
            updatedPhones[index] = {
                ...updatedPhones[index],
                [name]: value,
            };
            setFormData({...formData, phoneNumbers: updatedPhones});
        } else {
            setFormData({
                ...formData,
                [name]: isCheckbox ? (checked as boolean) : value,
            });
        }
    };

    const setDialCode = (index: number, dialCodeValue: string) => {
        const updatedPhones = [...formData.phoneNumbers];
        setDisableSaving(false);
        updatedPhones[index] = {
            ...updatedPhones[index],
            countryCode: dialCodeValue,
        };
        setHasUnsavedChanges(true);
        setFormData({...formData, phoneNumbers: updatedPhones});
    };

    const addPhoneNumber = () => {
        setFormData({
            ...formData,
            phoneNumbers: [...formData.phoneNumbers, {countryCode: "", phoneNumber: ""}],
        });
    };

    const removePhoneNumber = (index: number) => {
        if (formData.phoneNumbers[index].phoneNumber !== "") {
            setDisableSaving(false);
        }
        setHasUnsavedChanges(true);
        const updatedPhones = formData.phoneNumbers.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            phoneNumbers: updatedPhones.length ? updatedPhones : [{countryCode: "", phoneNumber: ""}]
        });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage(null);

        try {
            const dto = {
                street: formData.street,
                number: formData.number,
                zip: formData.zip,
                city: formData.city,
                country: formData.country,
                shareData: formData.shareData,
                phoneNumbers: formData.phoneNumbers
                    .filter(p => p.countryCode && p.phoneNumber)
                    .map(p => ({
                        countryCode: p.countryCode,
                        phoneNumber: p.phoneNumber,
                    })),
            };

            var newContactData = await upsertAccountContactData(account.id, dto);
            setAccountContactData(newContactData);
            setMessage({type: "success", text: t("common.messages.success")});
            setDisableSaving(true);
            setHasUnsavedChanges(false);
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    const renderPhoneRow = (phone: PhoneNumberDTO, index: number) => {
        const multiple = formData.phoneNumbers.length > 1;
        return (
            <PhoneNumberRow
                phone={phone}
                index={index}
                dialOptions={dialOptions}
                onChange={handleChange}
                setDialCode={setDialCode}
                removePhoneNumber={removePhoneNumber}
                multiple={multiple}
            />
        );
    };


    return (
        <div>
            <div>
                <h2>{t("menu.contact")}</h2>
            </div>

            <form onSubmit={handleSubmit} className="form-container">
                <MessageBox message={message}/>

                {/* Straße & Hausnummer */}
                <div className="form-group">
                    <label>{`${t("contact.fields.street")} & ${t("contact.fields.number")}`}</label>
                    <div className="form-row split-75-25">
                        <input
                            type="text"
                            name="street"
                            value={formData.street}
                            onChange={handleChange}
                            className="form-input"
                            required
                        />
                        <input
                            type="text"
                            name="number"
                            value={formData.number}
                            onChange={handleChange}
                            className="form-input"
                            required
                        />
                    </div>
                </div>

                {/* PLZ & Stadt */}
                <div className="form-group">
                    <label>{`${t("contact.fields.zip")} & ${t("contact.fields.city")}`}</label>
                    <div className="form-row split-25-75">
                        <input
                            type="text"
                            name="zip"
                            value={formData.zip}
                            onChange={handleChange}
                            className="form-input"
                            required
                        />
                        <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input"
                            required
                        />
                    </div>
                </div>

                {/* Land */}
                <div className="form-group">
                    <label>{t("contact.fields.country")}</label>
                    <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="form-input"
                        required
                    >
                        <option value="">{t("contact.fields.country")}</option>
                        {countryOptions.map(({iso2, name}: Country) => (
                            <option key={`${iso2}-${name}`} value={name}>
                                {isoToFlag(iso2)} {name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Telefonnummern */}
                <div className="form-group">
                    <label>{t("contact.fields.phone")}</label>
                    {formData.phoneNumbers.map((phone, index) => renderPhoneRow(phone, index))}
                    <button
                        type="button"
                        onClick={addPhoneNumber}
                        className="form-button-secondary"
                    >
                        {/*<span className="emoji">&#x2795;</span>*/}
                        {t("common.button.addPhone")}
                    </button>
                </div>

                {/* Share */}
                <label>
                    <input
                        type="checkbox"
                        name="shareData"
                        checked={formData.shareData}
                        onChange={handleChange}
                    />
                    {t("contact.fields.share")}
                </label>

                {/* Submit */}
                <button
                    type="submit"
                    disabled={disableSaving}
                    className="form-button">
                    {t("common.button.save")}
                </button>
            </form>
        </div>
    );
}

export default ContactData;